import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * A custom element that allows the user to select a quantity.
 *
 * Product page:
 * - Minimum quantity remains the product minimum (normally 1)
 *
 * Cart / cart drawer:
 * - Minus at quantity 1 changes quantity to 0
 * - Quantity 0 is dispatched to the cart
 * - Parent cart component can then remove the line
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} minusButton
 * @property {HTMLButtonElement} plusButton
 *
 * @extends {Component}
 */
export class QuantitySelectorComponent extends Component {
  requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];

  serverDisabledMinus = false;
  serverDisabledPlus = false;
  initialized = false;

  connectedCallback() {
    super.connectedCallback();

    const { quantityInput, minusButton, plusButton } = this.refs;

    const isCartLine = Boolean(quantityInput.dataset.cartLine);

    /*
     * IMPORTANT:
     * On product pages we preserve Shopify's server-disabled state.
     *
     * In the cart/cart drawer we must allow the minus button at
     * quantity 1 so the quantity can become 0 and remove the line.
     */
    if (!isCartLine && minusButton.disabled) {
      this.serverDisabledMinus = true;
    }

    if (plusButton.disabled) {
      this.serverDisabledPlus = true;
    }

    /*
     * If this is a cart line, make sure an initially-disabled
     * minus button is allowed to be managed by JS.
     */
    if (isCartLine) {
      this.serverDisabledMinus = false;
      minusButton.disabled = false;
    }

    this.initialized = true;
    this.updateButtonStates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Returns true when the quantity selector belongs to a cart line.
   *
   * Cart quantity inputs already provide data-cart-line, which is
   * also used when QuantitySelectorUpdateEvent is dispatched.
   *
   * @returns {boolean}
   */
  isCartLine() {
    return Boolean(this.refs.quantityInput.dataset.cartLine);
  }

  /**
   * Gets the effective minimum.
   *
   * Product page:
   * Uses configured minimum, normally 1.
   *
   * Cart:
   * Allows 0 so quantity 1 → 0 removes the cart item.
   *
   * @returns {number}
   */
  getEffectiveMin() {
    const { min } = this.getCurrentValues();

    if (this.isCartLine()) {
      return 0;
    }

    return min;
  }

  /**
   * Updates cart quantity and refreshes component state.
   *
   * @param {number} cartQty
   */
  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute(
      'data-cart-quantity',
      cartQty.toString()
    );

    this.updateCartQuantity();
  }

  /**
   * Checks if the current quantity can be added to cart
   * without exceeding max.
   *
   * @returns {{
   *   canAdd: boolean,
   *   maxQuantity: number|null,
   *   cartQuantity: number,
   *   quantityToAdd: number
   * }}
   */
  canAddToCart() {
    const { max, cartQuantity, value } = this.getCurrentValues();

    const quantityToAdd = value;

    const wouldExceedMax =
      max !== null &&
      cartQuantity + quantityToAdd > max;

    return {
      canAdd: !wouldExceedMax,
      maxQuantity: max,
      cartQuantity,
      quantityToAdd,
    };
  }

  /**
   * Gets current quantity value.
   *
   * @returns {string}
   */
  getValue() {
    return this.refs.quantityInput.value;
  }

  /**
   * Sets current quantity value.
   *
   * @param {string} value
   */
  setValue(value) {
    this.refs.quantityInput.value = value;
  }

  /**
   * Updates min/max/step constraints and snaps
   * the current value to a valid increment.
   *
   * @param {string} min
   * @param {string|null} max
   * @param {string} step
   */
  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;

    const currentValue =
      parseInt(quantityInput.value) || 0;

    quantityInput.min = min;

    if (max) {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute('max');
    }

    quantityInput.step = step;

    const configuredMin =
      parseIntOrDefault(min, 1);

    const effectiveMin =
      this.isCartLine()
        ? 0
        : configuredMin;

    const newStep =
      parseIntOrDefault(step, 1);

    const effectiveMax =
      this.getEffectiveMax();

    let newValue = currentValue;

    /*
     * Only snap positive quantities to the configured
     * product increments.
     *
     * Zero is valid in the cart because it means remove.
     */
    if (
      newValue !== 0 &&
      (newValue - configuredMin) % newStep !== 0
    ) {
      newValue =
        configuredMin +
        Math.floor(
          (newValue - configuredMin) / newStep
        ) *
          newStep;
    }

    newValue = Math.max(
      effectiveMin,
      Math.min(
        effectiveMax ?? Infinity,
        newValue
      )
    );

    if (newValue !== currentValue) {
      quantityInput.value =
        newValue.toString();
    }

    this.updateButtonStates();
  }

  /**
   * Gets current values from DOM.
   *
   * @returns {{
   *   min: number,
   *   max: number|null,
   *   step: number,
   *   value: number,
   *   cartQuantity: number
   * }}
   */
  getCurrentValues() {
    const { quantityInput } = this.refs;

    return {
      min: parseIntOrDefault(
        quantityInput.min,
        1
      ),

      max: parseIntOrDefault(
        quantityInput.max,
        null
      ),

      step: parseIntOrDefault(
        quantityInput.step,
        1
      ),

      value: parseIntOrDefault(
        quantityInput.value,
        0
      ),

      cartQuantity: parseIntOrDefault(
        quantityInput.getAttribute(
          'data-cart-quantity'
        ),
        0
      ),
    };
  }

  /**
   * Gets effective maximum value.
   *
   * @returns {number|null}
   */
  getEffectiveMax() {
    const {
      max,
      cartQuantity,
      min,
    } = this.getCurrentValues();

    if (max === null) {
      return null;
    }

    /*
     * Cart line already represents the existing item.
     * Product page represents quantity being added.
     */
    if (this.isCartLine()) {
      return max;
    }

    return Math.max(
      max - cartQuantity,
      min
    );
  }

  /**
   * Updates plus/minus button states.
   */
  updateButtonStates() {
    const {
      minusButton,
      plusButton,
    } = this.refs;

    const { value } =
      this.getCurrentValues();

    const effectiveMin =
      this.getEffectiveMin();

    const effectiveMax =
      this.getEffectiveMax();

    /*
     * CART:
     * quantity 1 must still have an enabled minus button
     * because 1 → 0 means remove.
     *
     * Product page:
     * normal minimum behaviour remains unchanged.
     */
    if (!this.serverDisabledMinus) {
      minusButton.disabled =
        value <= effectiveMin;
    }

    if (!this.serverDisabledPlus) {
      plusButton.disabled =
        effectiveMax !== null &&
        value >= effectiveMax;
    }
  }

  /**
   * Updates quantity by given step.
   *
   * @param {number} stepMultiplier
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } =
      this.refs;

    const {
      step,
      value,
    } = this.getCurrentValues();

    const effectiveMin =
      this.getEffectiveMin();

    const effectiveMax =
      this.getEffectiveMax();

    const requestedValue =
      value + step * stepMultiplier;

    const newValue = Math.min(
      effectiveMax ?? Infinity,
      Math.max(
        effectiveMin,
        requestedValue
      )
    );

    quantityInput.value =
      newValue.toString();

    this.onQuantityChange();

    this.updateButtonStates();
  }

  /**
   * Handles quantity increase.
   *
   * @param {Event} event
   */
  increaseQuantity(event) {
    if (
      !(event.target instanceof HTMLElement)
    ) {
      return;
    }

    event.preventDefault();

    this.updateQuantity(1);
  }

  /**
   * Handles quantity decrease.
   *
   * @param {Event} event
   */
  decreaseQuantity(event) {
    if (
      !(event.target instanceof HTMLElement)
    ) {
      return;
    }

    event.preventDefault();

    this.updateQuantity(-1);
  }

  /**
   * Select input value on focus.
   *
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } =
      this.refs;

    if (
      !(event.target instanceof HTMLInputElement) ||
      document.activeElement !==
        quantityInput
    ) {
      return;
    }

    quantityInput.select();
  }

  /**
   * Handles manually-entered quantity.
   *
   * @param {Event} event
   */
  setQuantity(event) {
    if (
      !(event.target instanceof HTMLInputElement)
    ) {
      return;
    }

    event.preventDefault();

    const { quantityInput } =
      this.refs;

    const {
      min,
      step,
    } = this.getCurrentValues();

    const effectiveMin =
      this.getEffectiveMin();

    const effectiveMax =
      this.getEffectiveMax();

    let requestedQuantity =
      parseInt(event.target.value);

    if (
      Number.isNaN(requestedQuantity)
    ) {
      requestedQuantity =
        effectiveMin;
    }

    const quantity = Math.min(
      effectiveMax ?? Infinity,
      Math.max(
        effectiveMin,
        requestedQuantity
      )
    );

    /*
     * Zero is valid for cart removal.
     *
     * Only validate normal positive quantities
     * against the configured increment.
     */
    if (
      quantity !== 0 &&
      (quantity - min) % step !== 0
    ) {
      quantityInput.value =
        quantity.toString();

      quantityInput.reportValidity();

      return;
    }

    quantityInput.value =
      quantity.toString();

    this.onQuantityChange();

    this.updateButtonStates();
  }

  /**
   * Dispatches quantity update event.
   */
  onQuantityChange() {
    const { quantityInput } =
      this.refs;

    const newValue =
      parseInt(quantityInput.value);

    const cartLine =
      Number(
        quantityInput.dataset.cartLine
      ) || undefined;

    this.dispatchEvent(
      new QuantitySelectorUpdateEvent(
        newValue,
        cartLine
      )
    );
  }

  /**
   * Updates quantity from external cart changes.
   */
  updateCartQuantity() {
    const { quantityInput } =
      this.refs;

    const { value } =
      this.getCurrentValues();

    const effectiveMin =
      this.getEffectiveMin();

    const effectiveMax =
      this.getEffectiveMax();

    const clampedValue =
      Math.min(
        effectiveMax ?? Infinity,
        Math.max(
          effectiveMin,
          value
        )
      );

    if (clampedValue !== value) {
      quantityInput.value =
        clampedValue.toString();
    }

    this.updateButtonStates();
  }

  /**
   * Gets quantity input.
   *
   * @returns {HTMLInputElement}
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error(
        'Missing quantityInput in QuantitySelectorComponent'
      );
    }

    return this.refs.quantityInput;
  }
}

if (
  !customElements.get(
    'quantity-selector-component'
  )
) {
  customElements.define(
    'quantity-selector-component',
    QuantitySelectorComponent
  );
}